/**
 * Platform marker badge shared between `CampaignCard` (Google) and
 * `MetaCampaignCard` (Meta). Renders a small icon indicating which ad
 * platform a campaign belongs to.
 *
 * ── ASSETS ─────────────────────────────────────────────────────────────
 *
 * Three official PNG marks live at
 * `frontend-react/src/assets/images/platform-logos/`:
 *
 *   - `google-ads.png` — Google Ads color icon, downloaded from
 *     Google's brand resource center. Unmodified except for uniform
 *     downscale (see below).
 *   - `facebook.png` — Facebook circular logo (Meta brand resource
 *     center → Facebook → primary logo). Unmodified except for uniform
 *     downscale.
 *   - `instagram.png` — Instagram gradient glyph (Meta brand resource
 *     center → Instagram → glyph gradient). Unmodified except for
 *     uniform downscale.
 *
 * All three were resized from their downloaded master resolutions to
 * a uniform 256×256 at build-prep time using uniform LANCZOS
 * downscale. Resizing is NOT modification per any of the three brand
 * programs — the modification prohibitions apply to recoloring,
 * cropping, distortion, drop shadows, and similar visual changes, not
 * to dimension scaling, which every UI does. See
 * `frontend-react/src/assets/images/platform-logos/README.md` for the
 * source URLs + per-program compliance notes.
 *
 * ── DESIGN ─────────────────────────────────────────────────────────────
 *
 * - **Meta badge** renders Facebook + Instagram side-by-side. Meta
 *   ads run on Facebook, Instagram, and Audience Network; the FB+IG
 *   pair is the most honest two-icon representation. Each icon is
 *   ~22 px square; the pair sits in a flex row with a small gap.
 * - **Google badge** renders the single Google Ads icon at ~22 px
 *   square.
 * - Each `<img>` carries an empty `alt=""` because the wrapping
 *   `<span role="img" aria-label="...">` provides the accessible
 *   name. Decorative duplicate alt would announce twice on screen
 *   readers.
 * - `title` attribute provides the sighted-user tooltip.
 * - Reads from the campaign's `platform_source` field which the saved
 *   app_config sets on every Google entry (`'Google Ads'`) and every
 *   Meta entry (`'Meta Ads'`). Assignment sites:
 *   - Google: `src/flouri/account_configuration.py:839`
 *     (`campaign_data['platform_source'] = 'Google Ads'` inside the
 *     per-campaign loop of `construct_app_configuration_json__google_ads`).
 *   - Meta: `src/flouri/account_configuration.py:2580`
 *     (`'platform_source': 'Meta Ads'` in the dict literal returned by
 *     `construct_app_configuration_json__meta_ads`).
 */
import { cn } from '@/utils/cn';

import googleAdsLogo from '@/assets/images/platform-logos/google-ads.png';
import facebookLogo from '@/assets/images/platform-logos/facebook.png';
import instagramLogo from '@/assets/images/platform-logos/instagram.png';

type PlatformSource = 'Google Ads' | 'Meta Ads';

interface PlatformBadgeProps {
  platform: PlatformSource | string | null | undefined;
  /** Sizing override (height in pixels). Default 22 px — matches the
   * campaign-card metric row baseline. */
  heightPx?: number;
  className?: string;
}

export function PlatformBadge({
  platform,
  heightPx = 22,
  className,
}: PlatformBadgeProps) {
  if (!platform) return null;

  const isMeta = platform === 'Meta Ads';
  const isGoogle = platform === 'Google Ads';
  if (!isMeta && !isGoogle) return null;

  const ariaLabel = isMeta ? 'Meta Ads platform' : 'Google Ads platform';
  const tooltip = isMeta ? 'Meta Ads (Facebook + Instagram)' : 'Google Ads';

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      title={tooltip}
      className={cn('inline-flex items-center gap-1', className)}
      style={{ height: heightPx }}
    >
      {isMeta ? (
        <>
          <img
            src={facebookLogo}
            alt=""
            height={heightPx}
            style={{ height: heightPx, width: heightPx }}
          />
          <img
            src={instagramLogo}
            alt=""
            height={heightPx}
            style={{ height: heightPx, width: heightPx }}
          />
        </>
      ) : (
        <img
          src={googleAdsLogo}
          alt=""
          height={heightPx}
          style={{ height: heightPx, width: heightPx }}
        />
      )}
    </span>
  );
}
